import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../auth/AuthProvider';
import { changeSubscriptionPlan } from '../services/subscriptionApi';

const INTERVAL_META = {
  month: { other: 'year', otherLabel: 'annual' },
  year: { other: 'month', otherLabel: 'monthly' },
};

export default function PlanSwitcher() {
  const { subscription, setSubscription } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(null);

  const active = subscription?.active ?? false;
  const provider = subscription?.provider;
  const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd ?? false;
  const meta = INTERVAL_META[subscription?.interval];

  if (!active || provider === 'APPLE' || cancelAtPeriodEnd || !meta) return null;

  const handleConfirm = async () => {
    setSwitching(true);
    setError(null);
    try {
      const updated = await changeSubscriptionPlan(meta.other);
      setSubscription(updated);
      setOpen(false);
    } catch {
      setError('We couldn’t change your plan. Please try again.');
    } finally {
      setSwitching(false);
    }
  };

  const handleClose = () => {
    if (switching) return;
    setOpen(false);
    setError(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); setError(null); }}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          color: 'var(--text-secondary)',
          font: 'inherit',
          cursor: 'pointer',
          textDecoration: 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'var(--text-primary)';
          e.currentTarget.style.textDecoration = 'underline';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'var(--text-secondary)';
          e.currentTarget.style.textDecoration = 'none';
        }}
      >
        {' · '}switch to {meta.otherLabel}
      </button>

      {open && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'var(--overlay-bg)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '24px',
          }}
          onClick={handleClose}
        >
          <div
            role="dialog"
            aria-labelledby="plan-switch-title"
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '16px',
              padding: '28px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: 'var(--shadow-lg)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'var(--accent-light)',
              color: 'var(--accent-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="17 1 21 5 17 9" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
            </div>

            <h3
              id="plan-switch-title"
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--text-primary)',
                margin: '0 0 12px',
                letterSpacing: '-0.01em',
              }}
            >
              Switch to {meta.otherLabel} billing?
            </h3>

            <p style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
              margin: '0 0 24px',
            }}>
              {meta.other === 'year'
                ? 'You’ll move to annual billing today, prorated for unused time on your monthly plan. Annual saves about 20%.'
                : 'You’ll move to monthly billing today. Unused time on your annual plan is credited automatically.'}
            </p>

            {error && (
              <p role="alert" style={{
                fontSize: '13px',
                color: 'var(--danger)',
                margin: '0 0 16px',
              }}>{error}</p>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={switching}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '10px',
                  background: 'var(--btn-primary-bg)',
                  color: 'var(--btn-primary-text)',
                  fontSize: '14px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: switching ? 'wait' : 'pointer',
                  opacity: switching ? 0.7 : 1,
                }}
              >
                {switching ? 'Switching…' : `Switch to ${meta.otherLabel}`}
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={switching}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '10px',
                  background: 'transparent',
                  color: 'var(--text-tertiary)',
                  fontSize: '14px',
                  fontWeight: 500,
                  border: '1px solid var(--border-subtle)',
                  cursor: switching ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
